import { ReactNode, useEffect, useState } from 'react'
import { Link, Navigate, useLocation } from 'react-router-dom'
import { FormattedMessage } from 'react-intl'
import { useSnapshot } from 'valtio/react'
import { useAppModule } from '../appContext.ts'
import { useLazyRef } from '../../shared/lib/hooks.ts'
import { urls } from '../../shared/urls.ts'
import { Button } from '../../shared/ui/button/button.tsx'

export function SubscriptionAccess(props: { children: ReactNode }) {
  const appModule = useAppModule()
  const { authStore } = appModule
  const auth = useSnapshot(authStore.state)
  const userId = auth.user?.user_id
  const subscriptionService = useLazyRef(() =>
    appModule.subscriptionService(),
  ).current
  const location = useLocation()
  // Account management and payment results must remain reachable without access.
  const accountPage =
    location.pathname === urls.profile() ||
    location.pathname.startsWith(`${urls.profile()}/`) ||
    location.pathname.startsWith('/payment/') ||
    location.pathname === urls.paymentSuccess ||
    location.pathname.startsWith(`${urls.paymentSuccess}/`)
  const needsSubscription = auth.isLogin && !auth.isAnon && !accountPage
  const [status, setStatus] = useState<
    'loading' | 'active' | 'inactive' | 'error'
  >('loading')
  const [retry, setRetry] = useState(0)

  useEffect(() => {
    if (!needsSubscription) return
    let cancelled = false
    setStatus('loading')
    void subscriptionService.getActiveSubscription().then(
      (subscription) => {
        if (!cancelled) setStatus(subscription ? 'active' : 'inactive')
      },
      () => {
        if (!cancelled) setStatus('error')
      },
    )
    return () => {
      cancelled = true
    }
  }, [subscriptionService, needsSubscription, userId, retry])

  if (!needsSubscription) return props.children
  if (status === 'loading') {
    return (
      <div role="status" className="p-24">
        <FormattedMessage id="billing.loading" />
      </div>
    )
  }
  if (status === 'error') {
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-16 p-24">
        <p role="alert">
          <FormattedMessage id="billing.accessCheckError" />
        </p>
        <Button
          bg="blue"
          onClick={() => {
            setRetry((value) => value + 1)
          }}
        >
          <FormattedMessage id="billing.retry" />
        </Button>
        <Link to={urls.subscriptions()} className="text-blue4 underline">
          <FormattedMessage id="subscription.choose" />
        </Link>
        <Link to={urls.profile()} className="text-blue4 underline">
          <FormattedMessage id="billing.account" />
        </Link>
      </div>
    )
  }
  if (status === 'inactive') {
    return <Navigate to={urls.subscriptions()} replace />
  }
  return props.children
}
